import type {
  OutlookTriggerDraft,
  OutlookTriggerFolder,
} from '@littlebird/sdk';

type OutlookFilterKey =
  | 'addresses'
  | 'domains'
  | 'subjectContains'
  | 'excludedAddresses'
  | 'excludedDomains'
  | 'excludedSubjectContains';

export type OutlookFilterField = {
  key: OutlookFilterKey;
  label: string;
  placeholder: string;
  /** Typing it adds a chip. Left out for subjects, which can contain commas. */
  separator?: string;
};

export const outlookDirectionLabel = (folders: OutlookTriggerFolder[]) => {
  if (folders.length > 1) return 'received or sent';
  return folders[0] === 'sentitems' ? 'sent' : 'received';
};

/** Who `addresses` and `domains` name: the sender of received mail, the recipients of sent mail. */
const peopleLabel = (folders: OutlookTriggerFolder[]) => {
  if (folders.length > 1) return 'From or to';
  return folders[0] === 'sentitems' ? 'To' : 'From';
};

export const outlookFilterFields = (
  folders: OutlookTriggerFolder[]
): OutlookFilterField[] => {
  const people = peopleLabel(folders);
  const except = `Except ${people.toLowerCase()}`;
  return [
    { key: 'addresses', label: people, placeholder: 'anyone', separator: ',' },
    {
      key: 'domains',
      label: `${people} domains`,
      placeholder: 'any domain',
      separator: ',',
    },
    {
      key: 'subjectContains',
      label: 'Subject contains',
      placeholder: 'any subject',
    },
    {
      key: 'excludedAddresses',
      label: except,
      placeholder: 'no one',
      separator: ',',
    },
    {
      key: 'excludedDomains',
      label: `${except} domains`,
      placeholder: 'no domain',
      separator: ',',
    },
    {
      key: 'excludedSubjectContains',
      label: 'Except subject contains',
      placeholder: 'no subject',
    },
  ];
};

/** The filters with values, labelled as the trigger row labels them. */
export const setOutlookFilters = (trigger: OutlookTriggerDraft) =>
  outlookFilterFields(trigger.folders)
    .map((field) => ({ label: field.label, values: trigger[field.key] }))
    .filter((filter) => filter.values.length > 0);
